import { addMinutes, format, isPast, isValid, isWithinInterval } from 'date-fns';
import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'usehooks-ts';

import type { Note } from '@/apps/Notes/stores/useNotesStore';

import NoteFormModal from '@/apps/Notes/components/NoteFormModal';
import { AuthPrompt } from '@/apps/Schedule/components/AuthPrompt';
import { EventCard } from '@/apps/Schedule/components/EventCard';
import { CalendarEvent, useScheduleStore } from '@/apps/Schedule/stores/useScheduleStore';
import LoadingSpinner from '@/common/components/LoadingSpinner';
import { useDateLocale } from '@/common/hooks/useDateLocale';
import { useAlertStore } from '@/common/stores/useAlertStore';
import { useWidgetLinksStore } from '@/common/stores/useWidgetLinksStore';
import { useWidgetStore } from '@/common/stores/useWidgetStore';
import { LINK_TYPES } from '@/common/utils/constants';

const POLL_INTERVAL_MS = 60 * 1000;
const ALERT_CHECK_INTERVAL_MS = 10 * 1000;
const NOTIFIED_EVENTS_KEY = 'notifiedEvents';

export default function Schedule() {
  const { t } = useTranslation();
  const { locale, timeFormat } = useDateLocale();
  const { events, loading, authenticated, authenticate, checkAuthState, fetchEvents } =
    useScheduleStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const scheduleRef = useRef<HTMLDivElement>(null);
  const fetchLinks = useWidgetLinksStore((state) => state.fetchLinks);
  const addLink = useWidgetLinksStore((state) => state.addLink);
  const widgets = useWidgetStore((state) => state.widgets);

  const notesWidgetId = useMemo(() => {
    return widgets.find((w) => w.widget_id === 'notes')?.id ?? null;
  }, [widgets]);

  const [eventToAddNote, setEventToAddNote] = useState<CalendarEvent | null>(null);
  const [editNote, setEditNote] = useState<Pick<Note, 'id' | 'title' | 'content'> | null>(null);

  const handleAddNoteClick = useCallback((event: CalendarEvent) => {
    setEventToAddNote(event);
    setEditNote(null);
  }, []);

  const handleEditNoteClick = useCallback((note: Pick<Note, 'id' | 'title' | 'content'>) => {
    setEditNote(note);
    setEventToAddNote(null);
  }, []);

  const handleNoteModalClose = useCallback(() => {
    setEventToAddNote(null);
    setEditNote(null);
  }, []);

  const handleNoteSaved = useCallback(
    async (note: Note) => {
      if (eventToAddNote) {
        await addLink(LINK_TYPES.SCHEDULE_EVENT, eventToAddNote.id, LINK_TYPES.NOTE, note.id);
      }
    },
    [eventToAddNote, addLink],
  );

  const getNotifiedEvents = useCallback((): string[] => {
    const stored = localStorage.getItem(NOTIFIED_EVENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  }, []);

  const markEventAsNotified = useCallback(
    (eventId: string) => {
      const notifiedEvents = getNotifiedEvents();
      if (!notifiedEvents.includes(eventId)) {
        localStorage.setItem(NOTIFIED_EVENTS_KEY, JSON.stringify([...notifiedEvents, eventId]));
      }
    },
    [getNotifiedEvents],
  );

  const handleUpcomingMeeting = useCallback(
    (event: CalendarEvent) => {
      if (event.isAllDay) return;

      const startDate = new Date(event.startDateTime);
      if (!isValid(startDate)) return;

      const isUpcoming = isWithinInterval(new Date(), {
        start: addMinutes(startDate, -5),
        end: startDate,
      });

      if (!isUpcoming) return;

      const notifiedEvents = getNotifiedEvents();

      if (notifiedEvents.includes(event.id)) return;

      const alertComponent = event.meetLink && (
        <a
          className="inline-block mt-2 px-4 py-2 bg-black text-white rounded hover:bg-zinc-700 transition-colors"
          href={event.meetLink}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t('schedule.joinMeeting')}
        </a>
      );

      showAlert({
        type: 'info',
        title: t('schedule.meetingIncoming'),
        showNotification: true,
        message: t('schedule.meetingStartsIn5', { title: event.title }),
        duration: 7 * 60,
        component: alertComponent,
      });

      markEventAsNotified(event.id);
    },
    [showAlert, getNotifiedEvents, markEventAsNotified, t],
  );

  const checkUpcomingMeetings = useCallback(() => {
    if (!events.length) return;
    events.forEach(handleUpcomingMeeting);
  }, [events, handleUpcomingMeeting]);

  useEffect(() => {
    const clearNotifiedEvents = () => {
      const now = new Date();
      if (now.getHours() === 0 && now.getMinutes() === 0) {
        localStorage.removeItem(NOTIFIED_EVENTS_KEY);
      }
    };

    const interval = setInterval(clearNotifiedEvents, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useInterval(() => {
    if (authenticated) {
      checkUpcomingMeetings();
    }
  }, ALERT_CHECK_INTERVAL_MS);

  useInterval(() => {
    if (authenticated) {
      fetchEvents();
    }
  }, POLL_INTERVAL_MS);

  useEffect(() => {
    if (!authenticated) checkAuthState();
  }, [checkAuthState, authenticated]);

  useEffect(() => {
    if (authenticated) {
      fetchEvents();
    }
  }, [authenticated, fetchEvents]);

  useEffect(() => {
    if (authenticated) {
      fetchLinks();
    }
  }, [authenticated, fetchLinks]);

  const isEventPast = useCallback((event: CalendarEvent) => {
    const endDate = new Date(event.endDateTime);
    if (!isValid(endDate)) return false;
    return isPast(endDate);
  }, []);

  const findCurrentOrNextEvent = useCallback(() => {
    const now = new Date();
    return events.find((event) => {
      const startDate = new Date(event.startDateTime);
      return isValid(startDate) && startDate >= now;
    });
  }, [events]);

  useEffect(() => {
    if (!events.length || !scheduleRef.current) return;

    const scrollToEvent = () => {
      const currentOrNext = findCurrentOrNextEvent();
      if (!currentOrNext) return;

      const eventElement = document.getElementById(`event-${currentOrNext.id}`);

      if (eventElement) {
        eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    scrollToEvent();
    const interval = setInterval(scrollToEvent, 60 * 1000);

    return () => clearInterval(interval);
  }, [events, findCurrentOrNextEvent]);

  if (!authenticated) {
    return <AuthPrompt onAuthenticate={authenticate} />;
  }

  return (
    <div ref={scheduleRef} className="h-full overflow-auto relative py-2">
      {loading && <LoadingSpinner />}
      {events.length === 0 ? (
        <div className="h-full flex items-center justify-center">
          <p className="text-gray-500 text-sm">{t('calendarApp.noMeetingsToday')}</p>
        </div>
      ) : (
        <div className="space-y-3 max-w-2xl mx-auto">
          {events.map((event: CalendarEvent) => (
            <div
              key={event.id}
              className={`transition-opacity duration-300 ${
                isEventPast(event) ? 'opacity-40' : 'opacity-100'
              }`}
              id={`event-${event.id}`}
            >
              <EventCard
                event={event}
                hasNotesWidget={!!notesWidgetId}
                onAddNoteClick={notesWidgetId ? handleAddNoteClick : undefined}
                onEditNoteClick={notesWidgetId ? handleEditNoteClick : undefined}
              />
            </div>
          ))}
        </div>
      )}

      {notesWidgetId && (
        <NoteFormModal
          editNote={editNote}
          initialTitle={
            eventToAddNote
              ? (() => {
                  const start = new Date(eventToAddNote.startDateTime);
                  if (!isValid(start)) return eventToAddNote.title;
                  const datePart = eventToAddNote.isAllDay
                    ? format(start, 'MM/dd', { locale })
                    : format(start, `MM/dd ${timeFormat}`, { locale });
                  return `${eventToAddNote.title} - ${datePart}`;
                })()
              : undefined
          }
          isOpen={!!eventToAddNote || !!editNote}
          widgetId={notesWidgetId}
          onClose={handleNoteModalClose}
          onSaved={handleNoteSaved}
        />
      )}
    </div>
  );
}
